/** @jsxImportSource theme-ui @jsxRuntime classic */
import {
  Section,
  Container,
  Box,
  IntrinsicImage,
  Text,
  ArrowLink,
  Spacer,
  Link,
} from '@tishman/components';
import { H } from '@hzdg/sectioning';
import React, { useMemo } from 'react';

import { formatStoryCategory } from '../utils';

import type { ComponentPropsWithoutRef } from 'react';

const StoryHeroBlock = (
  props: { data: Queries.SanityStoryQuery } & ComponentPropsWithoutRef<
    typeof Section
  >,
): React.JSX.Element => {
  const { data } = props;

  const storyHeroProps = useMemo(() => {
    if (!data) throw new Error('Expected event data');
    if (!data?.story) throw new Error('Expected valid Story data');
    if (!data?.story?.titleAndSlug?.title)
      throw new Error('Expected valid Story title');

    if (!data?.story?.poster?.asset?.gatsbyImageData)
      throw new Error('Expected valid fluid poster');
    // if (!fluid?.aspectRatio)
    //   throw new Error('Expected valid fluid poster aspect ratio');
    // if (!fluid?.sizes) throw new Error('Expected valid fluid poster sizes');
    // if (!fluid?.src) throw new Error('Expected valid fluid poster src');
    // if (!fluid?.srcSet) throw new Error('Expected valid fluid poster srcSet');

    if (!data?.story?.formattedPublishAt)
      throw new Error('Expected valid Story publishAt date');
    if (!data?.story?.category)
      throw new Error('Expected valid story category');
    return {
      title: data.story.titleAndSlug.title,
      authors: data.story.authors,
      formattedPublishAt: data.story.formattedPublishAt,
      gatsbyImageData: data.story.poster.asset.gatsbyImageData,
      category: data.story.category,
    };
  }, [data]);

  const formattedCategory = formatStoryCategory(storyHeroProps.category);

  return (
    storyHeroProps && (
      <Section data-section="StoryHero" {...props}>
        <Container
          px={['24px']}
          py={[6, null, null, '80px']}
          sx={{ pt: ['25px', null, null, 4] }}
        >
          <ArrowLink
            href="/magazine/"
            label="Back to Stories"
            reverse
            sx={{
              display: 'flex',
              justifyContent: 'start',
              mb: [4, null, null, 6],
              fontWeight: 'book',
              fontSize: 2,
              letterSpacing: '0px',
            }}
          />
          <IntrinsicImage
            image={storyHeroProps.gatsbyImageData}
            maxWidth={920}
            ratio={920 / 1000}
            sx={{ mx: 'auto' }}
          />
          <Box sx={{ textAlign: 'center' }}>
            <H
              sx={{
                variant: 'text.heroTitle',
                fontFamily: 'headingSecondary',
                textAlign: 'center',
                mx: 'auto',
                maxWidth: 800,
                mt: [4, null, null, 6],
                mb: [3, null, null, '40px'],
              }}
            >
              {storyHeroProps.title}
            </H>
            <Text
              sx={{
                mb: 4,
                fontWeight: 'regular',
                fontSize: 1,
                textTransform: 'uppercase',
                letterSpacing: '2.48px',
                color: 'heroBackground',
              }}
            >
              {storyHeroProps.authors && storyHeroProps.authors.length > 0 && (
                <Spacer>{`By ${storyHeroProps.authors.join(' & ')}`}</Spacer>
              )}
              <Spacer>{storyHeroProps.formattedPublishAt}</Spacer>
            </Text>

            <Box
              key={storyHeroProps.category}
              sx={{
                'border': '1px solid #F8E9DB',
                'padding': '10px',
                'margin': '0 auto',
                'mt': [0, null, null, 6],
                'cursor': 'pointer',
                'width': 'fit-content',
                '&:hover': {
                  'backgroundColor': 'CREAM',
                  'a div': {
                    color: 'GREEN',
                  },
                },
              }}
            >
              <Link
                href={`/magazine/#${formattedCategory}`}
                sx={{ textDecoration: 'none' }}
              >
                <Text
                  sx={{
                    variant: 'text.storyTopic',
                  }}
                >
                  {storyHeroProps.category}
                </Text>
              </Link>
            </Box>
          </Box>
        </Container>
      </Section>
    )
  );
};

export default StoryHeroBlock;
